import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';

Modal.setAppElement('#root');

const LeaveAttendanceTracking = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [employees, setEmployees] = useState([]);

  const [newRecord, setNewRecord] = useState({
    employeeId: '',
    type: 'Attendance',
    date: new Date().toISOString().split('T')[0],
    status: 'Present',
    notes: '',
    userId: user?.uid || ''
  });
  const [editingRecord, setEditingRecord] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFetchingRecords, setIsFetchingRecords] = useState(true);
  const [hasFetchedRecords, setHasFetchedRecords] = useState(false);

  const recordsCollectionRef = collection(db, "leaveAttendanceRecords");
  const employeesCollectionRef = collection(db, "staffs");

  useEffect(() => {
    if (user && userData) {
      fetchEmployees();
      fetchRecords();
    }
  }, [user, userData]);

  useEffect(() => {
    const filtered = records.filter((record) =>
      employees.find(e => e.id === record.employeeId)?.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredRecords(filtered);
  }, [searchTerm, records, employees]);

  const fetchEmployees = async () => {
    if (!user?.uid) return;
    try {
      const q = query(employeesCollectionRef, where("active", "==", true));
      const data = await getDocs(q);
      const employeeList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setEmployees(employeeList);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setError("Failed to fetch employees");
      setShowModal(true);
    }
  };

  const fetchRecords = async () => {
    if (!user?.uid) return;
    
    setIsFetchingRecords(true);
    try {
      const q = query(recordsCollectionRef);
      const data = await getDocs(q);
      const recordList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setRecords(recordList);
      setHasFetchedRecords(true);
    } catch (error) {
      console.error("Error fetching records:", error);
      setError("Failed to fetch records");
      setShowModal(true);
    } finally {
      setIsFetchingRecords(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRecord({ ...newRecord, [name]: value });
  };

  const handleAddRecord = async () => {
    if (!newRecord.employeeId || !newRecord.date) {
      setError("Employee and date are required");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(recordsCollectionRef, {
        ...newRecord,
        userId: user?.uid,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Record added successfully");
      fetchRecords();
      resetNewRecord();
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add record");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateRecord = async () => {
    setUpdate(true);
    try {
      const recordDoc = doc(db, "leaveAttendanceRecords", editingRecord.id);
      await updateDoc(recordDoc, newRecord);
      setSuccessMessage("Record updated successfully");
      fetchRecords();
      resetNewRecord();
      setEditingRecord(null);
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update record");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const resetNewRecord = () => {
    setNewRecord({
      employeeId: '',
      type: 'Attendance',
      date: new Date().toISOString().split('T')[0],
      status: 'Present',
      notes: '',
      userId: user?.uid || ''
    });
  };

  const handleEditRecord = (recordToEdit) => {
    setEditingRecord(recordToEdit);
    setNewRecord({
      employeeId: recordToEdit.employeeId,
      type: recordToEdit.type,
      date: recordToEdit.date,
      status: recordToEdit.status,
      notes: recordToEdit.notes,
      userId: recordToEdit.userId
    });
    setIsEditModalOpen(true);
  };

  const handleSyncHRApp = () => {
    // Placeholder for HR app integration (e.g., BambooHR, Workday)
    console.log("Syncing with HR app...");
    setSuccessMessage("HR app sync initiated (placeholder)");
    setShowModal(true);
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Admin', 'Supreme'];

  const displayContent = () => {
    if (isFetchingRecords && !hasFetchedRecords) {
      return <p className="loading-text">Loading records...</p>;
    }
    
    if (filteredRecords.length === 0) {
      if (searchTerm) {
        return <p className="loading-text">No records match your search.</p>;
      } else {
        return <p className="loading-text">No records found. Add your first record.</p>;
      }
    }
    
    return filteredRecords.map((recordItem) => (
      <div key={recordItem.id} className="class-item">
        <div className="column gap-1">
          <p><strong>{employees.find(e => e.id === recordItem.employeeId)?.name || 'Unknown'}</strong><br />
            <span>Type: {recordItem.type}</span>&nbsp;-&nbsp;
            <span>Date: {recordItem.date}</span><br />
            <span>Status: {recordItem.status}</span><br />
            {recordItem.notes && <span>Notes: {recordItem.notes}</span>}
          </p>                        
        </div>
        <div className="row align-center justify-center gap-15">
          <button className="list-btn" onClick={() => handleEditRecord(recordItem)}>
            <i className="fas fa-edit"></i>
          </button>
        </div>
      </div>
    ));
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Leave & Attendance Tracking</h2>
            <Link to="#" className="home-link" onClick={() => fetchRecords()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <div className="row justify-center align-center gap-15 add-buttons margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Record</button>
              <button className="login-button login-button-d caps" onClick={handleSyncHRApp}>Sync with HR App</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by employee name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Record"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Record</h2>
        <form className="session-form">
          <select
            name="employeeId"
            className="input-field"
            value={newRecord.employeeId}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Employee</option>
            {employees.map(employee => (
              <option key={employee.id} value={employee.id}>{employee.name}</option>
            ))}
          </select>
          <select
            name="type"
            className="input-field"
            value={newRecord.type}
            onChange={handleInputChange}
          >
            <option value="Attendance">Attendance</option>
            <option value="Leave">Leave</option>
          </select>
          <input
            type="date"
            name="date"
            className="input-field"
            value={newRecord.date}
            onChange={handleInputChange}
            required
          />
          <select
            name="status"
            className="input-field"
            value={newRecord.status}
            onChange={handleInputChange}
          >
            <option value="Present">Present</option>
            <option value="Absent">Absent</option>
            <option value="On Leave">On Leave</option>
          </select>
          <textarea
            name="notes"
            className="input-field"
            placeholder="Notes"
            value={newRecord.notes}
            onChange={handleInputChange}
          />
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddRecord}>Add Record</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          resetNewRecord();
          setEditingRecord(null);
        }}
        contentLabel="Edit Record"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Record</h2>
        <form className="session-form">
          <select
            name="employeeId"
            className="input-field"
            value={newRecord.employeeId}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Employee</option>
            {employees.map(employee => (
              <option key={employee.id} value={employee.id}>{employee.name}</option>
            ))}
          </select>
          <select
            name="type"
            className="input-field"
            value={newRecord.type}
            onChange={handleInputChange}
          >
            <option value="Attendance">Attendance</option>
            <option value="Leave">Leave</option>
          </select>
          <input
            type="date"
            name="date"
            className="input-field"
            value={newRecord.date}
            onChange={handleInputChange}
            required
          />
          <select
            name="status"
            className="input-field"
            value={newRecord.status}
            onChange={handleInputChange}
          >
            <option value="Present">Present</option>
            <option value="Absent">Absent</option>
            <option value="On Leave">On Leave</option>
          </select>
          <textarea
            name="notes"
            className="input-field"
            placeholder="Notes"
            value={newRecord.notes}
            onChange={handleInputChange}
          />
          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
              setIsEditModalOpen(false);
              resetNewRecord();
              setEditingRecord(null);
            }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateRecord}>Update Record</button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default LeaveAttendanceTracking;